// react
import { FC, useEffect, useRef, useState } from 'react';

// types
import { Entity, User } from './@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser, ContextUserEntity } from './App.context';

// sse
import { SSEProvider } from './utils/sse/SSEProvider';

// router
import { createBrowserRouter, Link as RouterLink, RouterProvider } from 'react-router-dom';

// pages
import { PageAccount } from './components/pages/Account/Account';
import { PageAdminDatasets } from './components/pages/Admin/AdminDatasets';
import { PageBeneficiary } from './components/pages/Beneficiary/Beneficiary';
import { PageBeneficiaryV2 } from './components/pages/BeneficiaryV2/BeneficiaryV2';
import { PageBlockchainStatus } from './components/pages/BlockchainStatus/BlockchainStatus';
import { PageComponentsCategories } from './components/pages/ComponentsCategories/ComponentsCategories';
import { PageCondition } from './components/pages/Condition/Condition';
import { PageContract } from './components/pages/Contract/Contract';
import { PageCreateSpendPlan } from './components/pages/CreateSpendPlan/CreateSpendPlan';
import { PageDashboard } from './components/pages/Dashboard/Dashboard';
import { PageEntity } from './components/pages/Entity/Entity';
import { PageFeatures } from './components/pages/Features/Features';
import { PageInvoice } from './components/pages/Invoice/Invoice';
import { PageLogin } from './components/pages/Login/Login';
import { PageLoginPersona } from './components/pages/LoginPersona/LoginPersona';
import { PageOnboard } from './components/pages/Onboard/Onboard';
import { PagePlanningActivity } from './components/pages/PlanningActivity/PlanningActivity';
import { PageProject } from './components/pages/Project/Project';
import { PageProjects } from './components/pages/Projects/Projects';
import { PageSpendPlan } from './components/pages/SpendPlan/SpendPlan';
import { PageSupplier } from './components/pages/Supplier/Supplier';
import { PageSupplierV2 } from './components/pages/SupplierV2/SupplierV2';
import { PageTheme } from './components/pages/Theme/Theme';
import { PageVerificationGroup } from './components/pages/VerificationGroup/VerificationGroup';
import { PageVoucher } from './components/pages/Voucher/Voucher';
import { PageWizard } from './components/pages/Wizard/Wizard';
import { PageWorkPlan } from './components/pages/WorkPlan/WorkPlan';

// projects pages
import { Onboard as ProjectsOnboard } from './components/pages/Projects/Onboard/Onboard';
import { Table as ProjectsTable } from './components/pages/Projects/Table/Table';

// project pages
import { Accounts as ProjectAccounts } from './components/pages/Project/Accounts/Accounts';
import { Activities as ProjectActivities } from './components/pages/Project/Activities/Activities';
import { Acts as ProjectActs } from './components/pages/Project/Acts/Acts';
import { Beneficiaries as ProjectBeneficiaries } from './components/pages/Project/Beneficiaries/Beneficiaries';
import { Contracts as ProjectContracts } from './components/pages/Project/Contracts/Contracts';
import { Entities as ProjectEntities } from './components/pages/Project/Entities/Entities';
import { Financing as ProjectFinancing } from './components/pages/Project/Financing/Financing';
import { Ifr as ProjectIfr } from './components/pages/Project/Ifr/Ifr';
import { IfrPlan as ProjectIfrPlan } from './components/pages/Project/IfrPlan/IfrPlan';
import { ProjectOverviewRoute } from './components/pages/Project/Overview/ProjectOverviewRoute';
import { Reports as ProjectReports } from './components/pages/Project/Reports/Reports';
import { Settings as ProjectSettings } from './components/pages/Project/Settings/Settings';
import { Suppliers as ProjectSuppliers } from './components/pages/Project/Suppliers/Suppliers';
import { Transactions as ProjectTransactions } from './components/pages/Project/Transactions/Transactions';

// project accounts pages
import { Designated as ProjectAccountsDesignated } from './components/pages/Project/Accounts/Designated/Designated';
import { Local as ProjectAccountsLocal } from './components/pages/Project/Accounts/Local/Local';

// project entities pages
import { Details as EntitiesDetails } from './components/pages/Project/Entities/Details/Details';
import { Hierarchy as EntitiesHierarchy } from './components/pages/Project/Entities/Hierarchy/Hierarchy';

// project ifr pages
import { Categories as ProjectIfrCategories } from './components/pages/Project/Ifr/Categories/Categories';
import { Components as ProjectIfrComponents } from './components/pages/Project/Ifr/Components/Components';
import { Forecast as ProjectIfrForecast } from './components/pages/Project/Ifr/Forecast/Forecast';
import { Reconciliation as ProjectIfrReconciliation } from './components/pages/Project/Ifr/Reconciliation/Reconciliation';

// project v2 pages
import { ActivityMonitoring as ProjectV2ActivityMonitoring } from './components/pages/ProjectV2/ActivityMonitoring/ActivityMonitoring';
import { Activities as ProjectV2Activities } from './components/pages/ProjectV2/Activities/Activities';
import { Beneficiaries as ProjectV2Beneficiaries } from './components/pages/ProjectV2/Beneficiaries/Beneficiaries';
import { Chat as ProjectV2Chat } from './components/pages/ProjectV2/Chat/Chat';
import { Conditions as ProjectV2Conditions } from './components/pages/ProjectV2/Conditions/Conditions';
import { Contracts as ProjectV2Contracts } from './components/pages/ProjectV2/Contracts/Contracts';
import { CustomTemplates as ProjectV2CustomTemplates } from './components/pages/ProjectV2/CustomTemplates/CustomTemplates';
import { PageCustomTemplate as ProjectV2CustomTemplate } from './components/pages/ProjectV2/CustomTemplates/PageCustomTemplate';
import { DemonstrationReports as ProjectV2DemonstrationReports } from './components/pages/ProjectV2/DemonstrationReports/DemonstrationReports';
import { Documents as ProjectV2Documents } from './components/pages/ProjectV2/Documents/Documents';
import { FinanceOverview as ProjectV2Dashboard } from './components/pages/ProjectV2/FinanceOverview/FinanceOverview';
import { FundingEarmarks as ProjectV2FundingEarmarks } from './components/pages/ProjectV2/FundingEarmarks/FundingEarmarks';
import { Invoices as ProjectV2Invoices } from './components/pages/ProjectV2/Invoices/Invoices';
import { InvoicesForProcessing as ProjectV2InvoicesForProcessing } from './components/pages/ProjectV2/InvoicesForProcessing/InvoicesForProcessing';
import { Payments as ProjectV2Payments } from './components/pages/ProjectV2/Payments/Payments';
import { SpendPlan as ProjectV2SpendPlan } from './components/pages/ProjectV2/ProjectV2';
import { FundingEarmarksReport as ProjectV2FundingEarmarksReport } from './components/pages/ProjectV2/Reports/FundingEarmarksReport';
import { PaymentReport as ProjectV2PaymentReport } from './components/pages/ProjectV2/Reports/PaymentReport';
import { ProjectOperationsDashboard as ProjectV2ProjectOperationsDashboard } from './components/pages/ProjectV2/Reports/ProjectOperationsDashboard';
import { SpendPlanning as ProjectV2SpendPlanning } from './components/pages/ProjectV2/SpendPlanning/SpendPlanning';
import { Suppliers as ProjectV2Suppliers } from './components/pages/ProjectV2/Suppliers/Suppliers';
import { VerificationGroups as ProjectV2VerificationGroups } from './components/pages/ProjectV2/VerificationGroups/VerificationGroups';
import { WorkPlanning as ProjectV2WorkPlanning } from './components/pages/ProjectV2/WorkPlanning/WorkPlanning';
import { WorkPlans as ProjectV2WorkPlans } from './components/pages/ProjectV2/WorkPlanning/WorkPlans';

// project transaction pages
import { Advances as TransactionsAdvances } from './components/pages/Project/Transactions/Advances/Advances';
import { CashBenefits as TransactionsCashBenefits } from './components/pages/Project/Transactions/CashBenefits/CashBenefits';
import { NonCashBenefits as TransactionsNonCashBenefits } from './components/pages/Project/Transactions/NonCashBenefits/NonCashBenefits';
import { OperatingExpenses as TransactionsOperatingExpenses } from './components/pages/Project/Transactions/OperatingExpenses/OperatingExpenses';
import { Payments as TransactionsPayments } from './components/pages/Project/Transactions/Payments/Payments';
import { PaymentRefunds as TransactionsPaymentRefunds } from './components/pages/Project/Transactions/PaymentRefunds/PaymentRefunds';
import { SpecialCommitments as TransactionsSpecialCommitments } from './components/pages/Project/Transactions/SpecialCommitments/SpecialCommitments';
import { HistoricalTransactions as TransactionsHistorical } from './components/pages/Project/Transactions/HistoricalTransactions/HistoricalTransactions';
import { Reimbursements as TransactionsReimbursements } from './components/pages/Project/Transactions/Reimbursements/Reimbursements';
import { Transfers as TransactionsTransfers } from './components/pages/Project/Transactions/Transfers/Transfers';

// project suppliers pages
import { Details as SuppliersDetails } from './components/pages/Project/Suppliers/Details/Details';
import { Documents as SuppliersDocuments } from './components/pages/Project/Suppliers/Documents/Documents';

// project beneficiaries pages
import { Details as BeneficiariesDetails } from './components/pages/Project/Beneficiaries/Details/Details';
import { Documents as BeneficiariesDocuments } from './components/pages/Project/Beneficiaries/Documents/Documents';

// project reports financial pages
import { Plans as FinancialPlans } from './components/pages/Project/Reports/Financial/Plans/Plans';
import { Reports as FinancialReports } from './components/pages/Project/Reports/Financial/Reports/Reports';

// project settings pages
import { Benefits as ProjectSettingsBenefits } from './components/pages/Project/Settings/Benefits/Benefits';
import { CashInMethods as ProjectSettingsCashInMethods } from './components/pages/Project/Settings/CashInMethods/CashInMethods';
import { Categories as ProjectSettingsCategories } from './components/pages/Project/Settings/Categories/Categories';
import { Components as ProjectSettingsComponents } from './components/pages/Project/Settings/Components/Components';
import { CustomFields as ProjectSettingsCustomFields } from './components/pages/Project/Settings/CustomFields/CustomFields';
import { ExpenseTypes as ProjectSettingsExpenseTypes } from './components/pages/Project/Settings/ExpenseTypes/ExpenseTypes';
import { FormBuilder as ProjectSettingsFormBuilder } from './components/pages/Project/Settings/FormBuilder/FormBuilder';
import { Forms as ProjectSettingsForms } from './components/pages/Project/Settings/Forms/Forms';
import { Notifications as ProjectSettingsNotifications } from './components/pages/Project/Settings/Notifications/Notifications';
import { Permissions as ProjectSettingsPermissions } from './components/pages/Project/Settings/Permissions/Permissions';
import { Program as ProjectSettingsProgram } from './components/pages/Project/Settings/Program/Program';
import { Project as ProjectSettingsProject } from './components/pages/Project/Settings/Project/Project';
import { ReconciliationItems as ProjectSettingsReconciliationItems } from './components/pages/Project/Settings/ReconciliationItems/ReconciliationItems';
import { Tags as ProjectSettingsTags } from './components/pages/Project/Settings/Tags/Tags';
import { V2Onboarding as ProjectSettingsV2Onboarding } from './components/pages/Project/Settings/V2Onboarding/V2Onboarding';

// account page
import { Activity as AccountActivity } from './components/pages/Account/Activity/Activity';
import { Comments as AccountComments } from './components/pages/Account/Comments/Comments';

// entity page
import { Beneficiaries as EntityBeneficiaries } from './components/pages/Entity/Beneficiaries/Beneficiaries';
import { CashBenefits as EntityCashBenefits } from './components/pages/Entity/CashBenefits/CashBenefits';
import { Documents as EntityDocuments } from './components/pages/Entity/Documents/Documents';
import { NonCashBenefits as EntityNonCashBenefits } from './components/pages/Entity/NonCashBenefits/NonCashBenefits';
import { OpEx as EntityOpEx } from './components/pages/Entity/OpEx/OpEx';
import { Payments as EntityPayments } from './components/pages/Entity/Payments/Payments';
import { Suppliers as EntitySuppliers } from './components/pages/Entity/Suppliers/Suppliers';
import { Users as EntityUsers } from './components/pages/Entity/Users/Users';

// supplier page
import { Activities as SupplierActivities } from './components/pages/Supplier/Activities/Activities';
import { Contracts as SupplierContracts } from './components/pages/Supplier/Contracts/Contracts';
import { Documents as SupplierDocuments } from './components/pages/Supplier/Documents/Documents';
import { Payments as SupplierPayments } from './components/pages/Supplier/Payments/Payments';
import { Users as SupplierUsers } from './components/pages/Supplier/Users/Users';

// beneficiary page
import { CashBenefits as BeneficiaryCashBenefits } from './components/pages/Beneficiary/CashBenefits/CashBenefits';
import { Documents as BeneficiaryDocuments } from './components/pages/Beneficiary/Documents/Documents';
import { NonCashBenefits as BeneficiaryNonCashBenefits } from './components/pages/Beneficiary/NonCashBenefits/NonCashBenefits';
import { Users as BeneficiaryUsers } from './components/pages/Beneficiary/Users/Users';

// reports pages
import { Financial as ReportsFinancial } from './components/pages/Project/Reports/Financial/Financial';
import { FundFlow as ReportsFundFlow } from './components/pages/Project/Reports/FundFlow/FundFlow';
import { Soe as ReportsSoe } from './components/pages/Project/Reports/Soe/Soe';

// soe pages
import { Details as SoeDetails } from './components/pages/Project/Reports/Soe/Details/Details';
import { Summary as SoeSummary } from './components/pages/Project/Reports/Soe/Summary/Summary';

// ui
import { BannerProps } from './components/ui/Banner/Banner';

// styles
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// theme
import { theme } from './theme';

// router
const router = createBrowserRouter([
	{
		path: '/',
		element: <PageLogin />,
	},
	{
		path: '/onboard',
		element: <PageOnboard />,
	},
	{
		element: <PageLoginPersona />,
		path: '/personas',
	},
	{
		element: <PageDashboard />,
		path: '/dashboard/:tab?',
	},
	{
		element: <PageFeatures />,
		path: '/features/:id?',
	},
	{
		element: <PageProjects />,
		path: '/projects',
		children: [
			{
				element: <ProjectsTable />,
				index: true,
			},
			{
				element: <ProjectsTable />,
				path: 'details',
			},
			{
				element: <ProjectsOnboard />,
				path: 'onboard',
			},
		],
	},
	{
		path: '/project/:id?',
		element: <PageProject />,
		children: [
			{
				element: <ProjectOverviewRoute />,
				index: true,
			},
			{
				element: <ProjectOverviewRoute />,
				path: 'overview',
			},
			{
				element: <ProjectAccounts />,
				path: 'accounts',
				children: [
					{
						element: <ProjectAccountsDesignated />,
						index: true,
					},
					{
						element: <ProjectAccountsDesignated />,
						path: 'designated',
					},
					{
						element: <ProjectAccountsLocal />,
						path: 'local',
					},
				],
			},
			{
				element: <ProjectV2InvoicesForProcessing />,
				path: 'invoices-for-processing',
			},
			{
				element: <ProjectV2SpendPlan />,
				path: 'spend-plans',
				children: [
					{
						element: <ProjectV2Dashboard />,
						index: true,
					},
					// Work Planning routes (new structure)
					{
						element: <ProjectV2WorkPlanning />,
						path: 'work-planning',
						children: [
							{
								element: <ProjectV2WorkPlans />,
								index: true,
							},
							{
								element: <ProjectV2Activities />,
								path: 'activities',
							},
						],
					},
					{
						element: <PagePlanningActivity />,
					},
					{
						element: <PageWorkPlan />,
						path: 'work-planning/work-plan/:workPlanId',
					},
					// Components & Categories (read-only reference)
					{
						element: <PageComponentsCategories />,
						path: 'components-categories',
					},
					// Payment Conditions (renamed from conditions)
					{
						element: <ProjectV2Conditions />,
						path: 'payment-conditions',
					},
					{
						element: <PageCondition />,
						path: 'payment-condition/:conditionId',
					},
					// Legacy routes (keep for backward compatibility)
					{
						element: <PagePlanningActivity />,
						path: 'activity/:activityId',
					},
					{
						element: <ProjectV2Activities />,
						path: 'activities',
					},
					{
						element: <ProjectV2Beneficiaries />,
						path: 'beneficiaries',
						children: [
							{
								element: <BeneficiariesDetails />,
								index: true,
							},
							{
								element: <BeneficiariesDetails />,
								path: 'details',
							},
							{
								element: <BeneficiariesDocuments />,
								path: 'documents',
							},
						],
					},
					{
						element: <PageCondition />,
						path: 'condition/:conditionId',
					},
					{
						element: <ProjectV2Conditions />,
						path: 'conditions',
					},
					{
						element: <PageContract />,
						path: 'contract/:contractId',
					},
					{
						element: <ProjectV2Contracts />,
						path: 'contracts',
					},
					{
						element: <PageBeneficiaryV2 />,
						path: 'beneficiary/:beneficiaryId',
					},
					{
						element: <PageSupplierV2 />,
						path: 'supplier/:supplierId',
					},
					{
						element: <ProjectV2Suppliers />,
						path: 'suppliers',
						children: [
							{
								element: <SuppliersDetails />,
								index: true,
							},
							{
								element: <SuppliersDetails />,
								path: 'details',
							},
							{
								element: <SuppliersDocuments />,
								path: 'documents',
							},
						],
					},
					{
						element: <ProjectV2Invoices />,
						path: 'invoices',
					},
					{
						element: <ProjectV2VerificationGroups />,
						path: 'verification-groups',
					},
					{
						element: <PageVerificationGroup />,
						path: 'verification-group/:groupId',
					},
					{
						element: <PageInvoice />,
						path: 'invoice/:invoiceId',
					},
					{
						element: <PageWorkPlan />,
						path: 'work-plan/:workPlanId',
					},
					{
						element: <ProjectV2WorkPlans />,
						path: 'work-plans',
					},
					{
						element: <ProjectV2Payments />,
						path: 'vouchers',
					},
					{
						element: <PageVoucher />,
						path: 'voucher/:voucherId',
					},
					{
						element: <PageWizard />,
						path: 'wizard',
					},
					{
						element: <PageCreateSpendPlan />,
						path: 'spend-planning/create',
					},
					{
						element: <PageCreateSpendPlan />,
						path: 'spend-planning/edit/:spendPlanId',
					},
					{
						element: <PageSpendPlan />,
						path: 'spend-planning/:spendPlanId',
					},
					{
						element: <ProjectV2SpendPlanning />,
						path: 'spend-planning',
					},
					{
						element: <ProjectV2FundingEarmarks />,
						path: 'funding-earmarks',
					},
					// {
					// 	element: <SpendPlanProcurementPlans />,
					// 	path: 'procurement-plans',
					// },
					{
						element: <ProjectV2CustomTemplate />,
						path: 'custom-templates/:templateId',
					},
					{
						element: <ProjectV2CustomTemplates />,
						path: 'custom-templates',
					},
					{
						element: <ProjectV2DemonstrationReports />,
						path: 'demonstration-reports',
					},
					{
						element: <ProjectV2ActivityMonitoring />,
						path: 'activity-monitoring',
					},
					{
						element: <ProjectV2Documents />,
						path: 'documents',
					},
					{
						element: <ProjectV2Chat />,
						path: 'chat',
					},
					{
						element: <ProjectV2PaymentReport />,
						path: 'payment-report',
					},
					{
						element: <ProjectV2ProjectOperationsDashboard />,
						path: 'operations-dashboard',
					},
					{
						element: <ProjectV2FundingEarmarksReport />,
						path: 'funding-earmarks-report',
					},
				],
			},
			{
				path: 'beneficiary/:idBeneficiary',
				element: <PageBeneficiary />,
				children: [
					{
						element: <BeneficiaryCashBenefits />,
						index: true,
					},
					{
						element: <BeneficiaryCashBenefits />,
						path: 'cash-benefits',
					},
					{
						element: <BeneficiaryNonCashBenefits />,
						path: 'non-cash-benefits',
					},
					{
						element: <BeneficiaryUsers />,
						path: 'users',
					},
					{
						element: <BeneficiaryDocuments />,
						path: 'documents',
					},
				],
			},
			{
				path: 'account/:idAccount',
				element: <PageAccount />,
				children: [
					{
						element: <AccountActivity />,
						index: true,
					},
					{
						element: <AccountActivity />,
						path: 'activity',
					},
					{
						element: <AccountComments />,
						path: 'comments',
					},
				],
			},
			{
				element: <ProjectActivities />,
				path: 'activities',
			},
			{
				element: <ProjectActs />,
				path: 'acts',
			},
			{
				element: <ProjectContracts />,
				path: 'contracts',
			},
			{
				path: 'entity/:idEntity',
				element: <PageEntity />,
				children: [
					{
						element: <EntityUsers />,
						index: true,
					},
					{
						element: <EntityPayments />,
						path: 'payments',
					},
					{
						element: <EntityCashBenefits />,
						path: 'cash-benefits',
					},
					{
						element: <EntityNonCashBenefits />,
						path: 'non-cash-benefits',
					},
					{
						element: <EntityOpEx />,
						path: 'opex',
					},
					{
						element: <EntityUsers />,
						path: 'users',
					},
					{
						element: <EntitySuppliers />,
						path: 'suppliers',
					},
					{
						element: <EntityBeneficiaries />,
						path: 'beneficiaries',
					},
					{
						element: <EntityDocuments />,
						path: 'documents',
					},
				],
			},
			{
				element: <ProjectEntities />,
				path: 'entities',
				children: [
					{
						element: <EntitiesDetails />,
						index: true,
					},
					{
						element: <EntitiesDetails />,
						path: 'details',
					},
					{
						element: <EntitiesHierarchy />,
						path: 'hierarchy',
					},
				],
			},
			{
				element: <ProjectFinancing />,
				path: 'financing',
			},
			{
				element: <ProjectTransactions hasQuickActions={true} />,
				path: 'transactions',
				children: [
					{
						element: <TransactionsPayments />,
						index: true,
					},
					{
						element: <TransactionsAdvances />,
						path: 'advances',
					},
					{
						element: <TransactionsCashBenefits />,
						path: 'cash-benefits',
					},
					{
						element: <TransactionsNonCashBenefits />,
						path: 'non-cash-benefits',
					},
					{
						element: <TransactionsHistorical />,
						path: 'historical',
					},
					{
						element: <TransactionsOperatingExpenses />,
						path: 'opex',
					},
					{
						element: <TransactionsPayments />,
						path: 'payments',
					},
					{
						element: <TransactionsPaymentRefunds />,
						path: 'payment-refunds',
					},
					{
						element: <TransactionsSpecialCommitments />,
						path: 'special',
					},
					{
						element: <TransactionsReimbursements />,
						path: 'reimbursements',
					},
					{
						element: <TransactionsTransfers />,
						path: 'transfers',
					},
				],
			},
			{
				path: 'supplier/:idSupplier',
				element: <PageSupplier />,
				children: [
					{
						element: <SupplierPayments />,
						index: true,
					},
					{
						element: <SupplierActivities />,
						path: 'activities',
					},
					{
						element: <SupplierContracts />,
						path: 'contracts',
					},
					{
						element: <SupplierDocuments />,
						path: 'documents',
					},
					{
						element: <SupplierPayments />,
						path: 'payments',
					},
					{
						element: <SupplierUsers />,
						path: 'users',
					},
				],
			},
			{
				element: <ProjectSuppliers />,
				path: 'suppliers',
				children: [
					{
						element: <SuppliersDetails />,
						index: true,
					},
					{
						element: <SuppliersDetails />,
						path: 'details',
					},
					{
						element: <SuppliersDocuments />,
						path: 'documents',
					},
				],
			},
			{
				element: <ProjectBeneficiaries />,
				path: 'beneficiaries',
				children: [
					{
						element: <BeneficiariesDetails />,
						index: true,
					},
					{
						element: <BeneficiariesDetails />,
						path: 'details',
					},
					{
						element: <BeneficiariesDocuments />,
						path: 'documents',
					},
				],
			},
			{
				path: 'plan/:planId',
				element: <ProjectIfrPlan />,
			},
			{
				path: 'reports',
				children: [
					{
						element: <ProjectReports />,
						index: true,
					},
					{
						element: <ReportsSoe />,
						path: 'soe',
						children: [
							{
								element: <SoeDetails />,
								index: true,
							},
							{
								element: <SoeDetails />,
								path: 'details',
							},
							{
								element: <SoeSummary />,
								path: 'summary',
							},
						],
					},
					{
						element: <ReportsFinancial />,
						path: 'financial',
						children: [
							{
								element: <FinancialReports />,
								index: true,
							},
							{
								path: 'plans',
								element: <FinancialPlans />,
							},
							{
								element: <FinancialReports />,
								path: 'reports',
							},
						],
					},
					{
						element: <ReportsFundFlow />,
						path: 'flow',
					},
				],
			},
			{
				element: <ProjectIfr />,
				path: 'ifr/:idIfr',
				children: [
					{
						element: <ProjectIfrCategories />,
						index: true,
					},
					{
						element: <ProjectIfrCategories />,
						path: 'categories',
					},
					{
						element: <ProjectIfrComponents />,
						path: 'components',
					},
					{
						element: <ProjectIfrReconciliation />,
						path: 'reconciliation',
					},
					{
						element: <ProjectIfrForecast />,
						path: 'forecast',
					},
				],
			},
			{
				element: <ProjectSettings />,
				path: 'settings',
				children: [
					{
						element: <ProjectSettingsProject />,
						path: 'project',
					},
					{
						element: <ProjectSettingsProgram />,
						index: true,
					},
					{
						element: <ProjectSettingsBenefits />,
						path: 'benefits',
					},
					{
						element: <ProjectSettingsCashInMethods />,
						path: 'cash-in-methods',
					},
					{
						element: <ProjectSettingsCategories />,
						path: 'categories',
					},
					{
						element: <ProjectSettingsComponents />,
						path: 'components',
					},
					{
						element: <ProjectSettingsCustomFields />,
						path: 'custom-fields',
					},
					{
						element: <ProjectSettingsExpenseTypes />,
						path: 'expense-types',
					},
					{
						element: <ProjectSettingsForms />,
						path: 'forms',
					},
					{
						element: <ProjectSettingsFormBuilder />,
						path: 'form/:idForm',
					},
					{
						element: <ProjectSettingsPermissions />,
						path: 'permissions',
					},
					{
						element: <ProjectSettingsProgram />,
						path: 'program',
					},
					{
						element: <ProjectSettingsReconciliationItems />,
						path: 'reconciliation-items',
					},
					{
						element: <ProjectSettingsTags />,
						path: 'tags',
					},
					{
						element: <ProjectSettingsNotifications />,
						path: 'notifications',
					},
					{
						element: <ProjectSettingsV2Onboarding />,
						path: 'v2',
					},
				],
			},
		],
	},
	{
		path: '/theme',
		element: <PageTheme />,
	},
	{
		path: '/blockchain',
		element: <PageBlockchainStatus />,
	},
	{
		path: '/admin/datasets',
		element: <PageAdminDatasets />,
	},
	{
		path: '/login',
		element: <PageLogin />,
	},
]);

export const App: FC = () => {
	// hooks
	const { t } = useTranslation();

	// state
	const [banner, setBanner] = useState<BannerProps | null>(null);
	const [user, setUser] = useState<User | null>(null);
	const [userEntity, setUserEntity] = useState<Entity | null>(null);

	// refs
	const refBannerSet = useRef(true);

	// store the redirect url
	if (window.location.pathname !== '/') {
		const urlParams = new URLSearchParams(window.location.search);

		// delete specific parameters from the URL
		urlParams.delete('isLoginImmediate');

		// define and store redirect URL
		const urlParamsStr = urlParams.toString();
		const redirectUrl = `${window.location.pathname}${urlParamsStr ? `?${urlParamsStr}` : ''}`;
		sessionStorage.setItem('redirectUrl', String(redirectUrl));
	}

	// set user
	useEffect(() => {
		const sessionUser = sessionStorage.getItem('user');
		const user: User | null = sessionUser ? JSON.parse(sessionUser) : null;

		if (user) {
			// Get selected entity from session or default to first entity
			const selectedEntityIdFromSession = sessionStorage.getItem('userEntityId');

			const firstEntity =
				user.entities && user.entities.length > 0
					? typeof user.entities[0] === 'object'
						? user.entities[0]
						: null
					: null;

			// If we have a stored entity ID, find that entity in the user's entities
			let userEntityNew = firstEntity;

			if (selectedEntityIdFromSession && user.entities) {
				const entityId = parseInt(selectedEntityIdFromSession, 10);
				const foundEntity = user.entities.find(
					(entity) =>
						(typeof entity === 'object' && entity.id === entityId) ||
						(typeof entity === 'number' && entity === entityId)
				);

				if (foundEntity && typeof foundEntity === 'object') {
					userEntityNew = foundEntity;
				}
			}

			// Store the selected entity ID if we have one
			if (userEntityNew && typeof userEntityNew === 'object') {
				sessionStorage.setItem('userEntityId', String(userEntityNew.id));
			}

			// set user state
			setUser(user);
			setUserEntity(userEntityNew);
		}
	}, []);

	// set new features banner on mount
	useEffect(() => {
		if (refBannerSet.current) {
			return;
		}

		refBannerSet.current = true;

		setBanner({
			actions: [
				{
					children: t('learnMore'),
					color: 'white',
					component: RouterLink,
					onClick: () => {
						setBanner(null);
					},
					to: '/features',
					variant: 'text',
				},
			],
			hasClose: true,
			isGlobal: true,
			message: t('alert.newFeaturesAvailable'),
			severity: 'info',
			title: { children: t('newFeatures') },
		});
	}, [setBanner, t]);

	// scroll to top when banner changes
	useEffect(() => {
		if (banner) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, [banner]);

	return (
		<ContextBanner.Provider value={{ banner, setBanner }}>
			<ContextUser.Provider value={{ user, setUser }}>
				<ContextUserEntity.Provider value={{ userEntity, setUserEntity }}>
					<SSEProvider
						autoConnect={Boolean(user?.email && process.env.REACT_APP_SSE_URL)}
						token={sessionStorage.getItem('accessToken') || undefined}
						url={process.env.REACT_APP_SSE_URL}
						userId={user?.email}>
						<ThemeProvider theme={theme}>
							<CssBaseline />
							<RouterProvider router={router} />
						</ThemeProvider>
					</SSEProvider>
				</ContextUserEntity.Provider>
			</ContextUser.Provider>
		</ContextBanner.Provider>
	);
};
